import React, { useRef, useState } from "react";
import ShirtCard from "./ShirtCard";
import { ApiShirt } from "../../types/Shirt";
import type { Auction } from "../../services/auctionService";
import "./ShirtsCarousel.css";

interface ShirtItem {
  id?: number | string;
}

export interface ShirtsCarouselProps {
  items?: ShirtItem[];
  apiShirts?: ApiShirt[];
  auctionMap?: Map<string, Auction>;
  label?: string;
  gridId?: string;
  isAuction?: boolean;
  endCtaLabel?: string;
  onEndCtaClick?: () => void;
  showSeeMoreButton?: boolean;
  onSeeMore?: () => void;
  imageVariant?: "optimized" | "thumbnail";
}

export default function ShirtsCarousel({
  items = [],
  apiShirts,
  auctionMap,
  label = "Shirts",
  gridId = "shirtsCarousel",
  isAuction = false,
  endCtaLabel,
  onEndCtaClick,
  showSeeMoreButton = false,
  onSeeMore,
  imageVariant = "optimized",
}: ShirtsCarouselProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const effectiveLength = apiShirts?.length ?? items.length;
  const hasEndCta = Boolean(endCtaLabel && onEndCtaClick);
  const totalItems = effectiveLength + (hasEndCta ? 1 : 0);
  const [showLeftButton, setShowLeftButton] = useState(false);
  const [showRightButton, setShowRightButton] = useState(totalItems > 4);

  const checkScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    setShowLeftButton(container.scrollLeft > 0);
    setShowRightButton(
      container.scrollLeft < container.scrollWidth - container.clientWidth - 10,
    );
  };

  const scroll = (direction: "left" | "right") => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollAmount = 300;
    container.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });

    setTimeout(checkScroll, 300);
  };

  React.useEffect(() => {
    checkScroll();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", checkScroll);
      return () => container.removeEventListener("scroll", checkScroll);
    }
  }, [items, apiShirts, totalItems]);

  return (
    <section className="shirts-carousel" aria-label={label}>
      <div className="carousel-container">
        {showLeftButton && (
          <button
            className="carousel-button carousel-button--left"
            onClick={() => scroll("left")}
            aria-label="Vorige producten"
            title="Vorige"
          >
            <span>‹</span>
          </button>
        )}

        <div
          className="carousel-wrapper"
          ref={scrollContainerRef}
          id={gridId}
          role="region"
          aria-live="polite"
        >
          <div className="carousel-track">
            {apiShirts && apiShirts.length > 0
              ? apiShirts.map((shirt) => (
                  <div key={shirt.id} className="carousel-item">
                    <ShirtCard
                      shirt={shirt}
                      auction={auctionMap?.get(shirt.id)}
                      imageVariant={imageVariant}
                    />
                  </div>
                ))
              : items.map((item, index) => {
                  const id = item?.id ?? index;
                  return (
                    <div key={id} className="carousel-item">
                      <ShirtCard idx={Number(id)} imageVariant={imageVariant} />
                    </div>
                  );
                })}

            {hasEndCta && (
              <div className="carousel-item carousel-item--end-cta">
                <button
                  type="button"
                  className="carousel-end-cta"
                  onClick={onEndCtaClick}
                >
                  <span className="carousel-end-cta__label">{endCtaLabel}</span>
                  <span className="carousel-end-cta__arrow" aria-hidden="true">
                    &rarr;
                  </span>
                </button>
              </div>
            )}
          </div>
        </div>

        {showRightButton && (
          <button
            className="carousel-button carousel-button--right"
            onClick={() => scroll("right")}
            aria-label="Volgende producten"
            title="Volgende"
          >
            <span>›</span>
          </button>
        )}
      </div>

      {showSeeMoreButton && onSeeMore && (
        <div className="carousel-see-more-wrap">
          <button
            type="button"
            className="carousel-see-more-btn"
            onClick={onSeeMore}
          >
            Meer shirts bekijken
          </button>
        </div>
      )}
    </section>
  );
}
